import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { ifNonEmpty } from '../../base/directives';
import { WithFocus } from '../../base/elements';
import { eventListener } from '../../base/events';
import { ElementLogger } from '../../base/logger';
import { DEV_MODE } from '../../global/env';
import { toggleButtonElementStyles } from './styles';
export const TOGGLE_BUTTON_ELEMENT_TAG_NAME = 'vds-toggle-button';
/**
 * The foundation for any toggle button such as a `play-button` or `mute-button`.
 *
 *
 * @tagname vds-toggle-button
 * @csspart button - The button element (`<button>`).
 * @slot - Used to pass content into the toggle for showing pressed and not pressed states.
 * @example
 * ```html
 * <vds-toggle-button label="Some action">
 *   <div class="pressed">Pressed</div>
 *   <div class="not-pressed">Not Pressed</div>
 * </vds-toggle-button>
 * ```
 * @example
 * ```css
 * vds-toggle-button[pressed] .pressed {
 *   display: none;
 * }
 *
 * vds-toggle-button:not([pressed]) .not-pressed {
 *  display: none;
 * }
 * ```
 */
export class ToggleButtonElement extends WithFocus(LitElement) {
    constructor() {
        super(...arguments);
        // -------------------------------------------------------------------------------------------
        // Properties
        // -------------------------------------------------------------------------------------------
        /* c8 ignore next */
        this._logger = DEV_MODE && new ElementLogger(this);
        /**
         * Whether the toggle is currently in a `pressed` state.
         */
        this.pressed = false;
        /**
         * Whether the underlying button should be disabled (not-interactable).
         */
        this.disabled = false;
        // -------------------------------------------------------------------------------------------
        // Button
        // -------------------------------------------------------------------------------------------
        this._buttonRef = createRef();
    }
    static get styles() {
        return [toggleButtonElementStyles];
    }
    static get parts() {
        return ['button'];
    }
    /**
     * Whether the toggle is in the `pressed` state.
     */
    get isPressed() {
        return this.pressed;
    }
    // -------------------------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------------------------
    render() {
        return this._renderButton();
    }
    /**
     * The component's `<button>` element.
     */
    get buttonElement() {
        return this._buttonRef.value;
    }
    _renderButton() {
        return html `
      <button
        id="button"
        part="button"
        aria-label=${ifNonEmpty(this.label)}
        aria-pressed=${this.pressed ? 'true' : 'false'}
        aria-described-by=${ifNonEmpty(this.describedBy)}
        ?disabled=${this.disabled}
        @click=${this._handleButtonClick}
        ${ref(this._buttonRef)}
      >
        ${this._renderButtonChildren()}
      </button>
    `;
    }
    _renderButtonChildren() {
        return this._renderDefaultSlot();
    }
    _renderDefaultSlot() {
        return html `<slot></slot>`;
    }
    click() {
        var _a;
        if (this.disabled)
            return;
        (_a = this.buttonElement) === null || _a === void 0 ? void 0 : _a.click();
    }
    _handleButtonClick(event) {
        this.pressed = !this.pressed;
    }
    _handleButtonClickCapture(event) {
        if (this.disabled) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return false;
        }
        return true;
    }
}
__decorate([
    property({ type: Boolean, reflect: true })
], ToggleButtonElement.prototype, "pressed", void 0);
__decorate([
    property()
], ToggleButtonElement.prototype, "label", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ToggleButtonElement.prototype, "disabled", void 0);
__decorate([
    property({ attribute: 'described-by', reflect: true })
], ToggleButtonElement.prototype, "describedBy", void 0);
__decorate([
    eventListener('click', { capture: true })
], ToggleButtonElement.prototype, "_handleButtonClickCapture", null);
